import { useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import {
  TriangleIcon,
  CCWRefreshIcon,
  DocumentCopyIcon,
  EmailIcon,
  UploadShareIcon,
} from "@helloaudio/icons/twotone";
import { Label, LinkButtonBlue } from "@helloaudio/ui";
import { AIContentFields } from "../../../../constants/AiContentData";
import PropTypes from "prop-types";

AIContentGeneratedRow.propTypes = {
  data: PropTypes.object.isRequired,
  orderContent: PropTypes.func.isRequired,
};

// Toggle back to true once share link is implemented
const USE_SHARE_LINK = false;

function AIContentGeneratedRow({ data, orderContent }) {
  const formValue = useSelector((state) => state.basic);
  const currentUser = formValue?.localUser;
  const feedEpisode = formValue?.feedEpisode;

  const getMailtoLink = (data) => {
    const contentType = AIContentFields[data.contentType];
    const subject = `Your generated ${contentType} from Hello Audio`;
    const to = currentUser?.last_known_email;
    const body = `${feedEpisode?.title}\n\n${data.content}`;
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const onClickCopy = async (data) => {
    await navigator.clipboard.writeText(data.content);
    toast.success("Copied to clipboard!", { duration: 3000 });
  };

  const renderContent = () => {
    if (data.contentStatus === "ordered" || data.contentStatus === "processing") {
      return (
        <div
          className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-text-sm tw-font-Montserrat"
          style={{ fontStyle: "italic" }}
        >
          Processing... This text will automatically update when completed.
        </div>
      );
    }
    if (data.contentStatus === "error") {
      return (
        <div
          className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-text-sm tw-font-Montserrat"
          style={{ fontStyle: "italic" }}
        >
          An error occurred processing your media. Please try again.
        </div>
      );
    }
    if (data.contentStatus === "complete") {
      let contentPreview = "There was no content generated for this section.";

      // if the drawer is closed show 1-2 sentences
      if (openDrawer) {
        return (
          <div className="tw-flex tw-items-center tw-justify-between tw-my-4 tw-text-sm tw-font-Montserrat tw-whitespace-pre-wrap">
            {data?.content ?? contentPreview}
          </div>
        );
      } else {
        return (
          <div
            className="ai-content-preview-row tw-flex tw-items-center tw-justify-between tw-mt-4 tw-text-sm tw-font-Montserrat tw-cursor-pointer tw-whitespace-pre-wrap"
            onClick={() => setOpenDrawer(true)}
          >
            {data?.content?.slice(0, 200) ?? contentPreview}...
          </div>
        );
      }
    }
  };

  const [openDrawer, setOpenDrawer] = useState(false);
  return (
    <>
      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-cursor-point">
        <div className="tw-flex tw-items-center">
          {data.contentStatus === "complete" && (
            <TriangleIcon
              onClick={() => setOpenDrawer(!openDrawer)}
              className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary"
              style={{
                marginRight: "10px",
                transition: "transform 0.2s",
                cursor: "pointer",
                transform: !openDrawer ? "rotate(-90deg)" : "rotate(0deg)",
              }}
            />
          )}
          <Label
            className="tw-cursor-pointer tw-my-4 tw-text-sm"
            onClick={() => setOpenDrawer(!openDrawer)}
          >
            {AIContentFields[data.contentType]}
          </Label>
        </div>
        {data.contentStatus === "complete" ? (
          <div className="tw-flex tw-items-center ai-post-buttongroup">
            <LinkButtonBlue
              onClick={(e) => {
                e.preventDefault();
                orderContent(data);
              }}
              className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer tw-ml-3"
            >
              <CCWRefreshIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
              <div className="tw-flex tw-text-sm text-btn-primary">Regenerate</div>
            </LinkButtonBlue>
            <LinkButtonBlue
              onClick={(e) => {
                e.preventDefault();
                onClickCopy(data);
              }}
              className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer tw-ml-3"
            >
              <DocumentCopyIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
              <div className="tw-flex tw-text-sm text-btn-primary">Copy</div>
            </LinkButtonBlue>
            <LinkButtonBlue className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer tw-ml-3">
              <EmailIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
              <div className="tw-flex tw-text-base">
                <a
                  className="tw-text-sm text-btn-primary hover:tw-no-underline"
                  href={getMailtoLink(data)}
                  rel="noreferrer"
                  target="_blank"
                >
                  Email
                </a>
              </div>
            </LinkButtonBlue>
            {USE_SHARE_LINK && (
              <LinkButtonBlue
                onClick={(e) => {
                  e.preventDefault();
                }}
                className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer tw-ml-3"
              >
                <UploadShareIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
                <div className="tw-flex tw-text-sm text-btn-primary">Share</div>
              </LinkButtonBlue>
            )}
          </div>
        ) : (
          <div className="tw-flex tw-items-center ai-post-buttongroup">
            <LinkButtonBlue
              onClick={(e) => {
                e.preventDefault();
                orderContent(data);
              }}
              className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer tw-ml-3"
            >
              <CCWRefreshIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
              <div className="tw-flex tw-text-sm text-btn-primary">Regenerate</div>
            </LinkButtonBlue>
          </div>
        )}
      </div>

      {renderContent()}

      {data.contentStatus === "complete" && (
        <div className="tw-flex tw-items-center tw-justify-around ai-post-buttongroup-mobile">
          <LinkButtonBlue
            onClick={(e) => {
              e.preventDefault();
              onClickCopy(data);
            }}
            className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer"
          >
            <DocumentCopyIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
            <div className="tw-flex tw-text-sm text-btn-primary">Copy</div>
          </LinkButtonBlue>
          <LinkButtonBlue className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer">
            <EmailIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
            <div className="tw-flex tw-text-base">
              <a
                className="tw-text-sm text-btn-primary hover:tw-no-underline"
                href={getMailtoLink(data)}
                rel="noreferrer"
                target="_blank"
              >
                Email
              </a>
            </div>
          </LinkButtonBlue>
          <LinkButtonBlue
            onClick={(e) => {
              e.preventDefault();
            }}
            className="text-btn-primary tw-flex tw-items-center tw-cursor-pointer"
          >
            <UploadShareIcon className="tw-p-0 tw-w-4 tw-h-4 text-btn-primary tw-mr-2.5" />
            <div className="tw-flex tw-text-sm text-btn-primary">Share</div>
          </LinkButtonBlue>
        </div>
      )}
    </>
  );
}

export default AIContentGeneratedRow;
